import { useCallback, useMemo } from 'react';

import { useLazyQuery } from '@apollo/client/react';
import { useGraphQLQuery } from '@betterment/graphql';

import { getLazyLoadingStatus, getLoadingStatus } from '~/b4b/graphql/helpers';

import {
  GetPlanManagerSidebarDocument,
  GetPlanManagerSidebarQuery,
} from './graphql/query.getPlanManagerSidebar.generated';
import { GetPlanManagerSidebarEmployersWithTasksDocument } from './graphql/query.getPlanManagerSidebarEmployersWithTasks.generated';
import { createTaskCountsFromEmployers } from './helpers';

export const useSidebarDataQuery = (employerId: string) => {
  const query = useGraphQLQuery(GetPlanManagerSidebarDocument, {
    variables: { employerId },
  });

  const { data: rawData, refetch } = query;
  const data = rawData as GetPlanManagerSidebarQuery | undefined;

  const loadingStatus = getLoadingStatus(query);

  const employers = useMemo(() => {
    const unsortedEmployers = data?.employers.filter(Boolean) || [];
    return unsortedEmployers.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const currentEmployer = data?.employer;
  const personalProfile = data?.personalProfile;

  const reload = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    employers,
    currentEmployer,
    personalProfile,
    loadingStatus,
    reload,
  };
};

export const useEmployersWithTasksQuery = () => {
  const [loader, query] = useLazyQuery(GetPlanManagerSidebarEmployersWithTasksDocument);

  const { data } = query;

  const tasksLoadingStatus = getLazyLoadingStatus(query);

  const taskCounts: Record<string, number> = useMemo(
    () => createTaskCountsFromEmployers(data?.employers || []),
    [data]
  );

  const loadEmployersWithTasks = useCallback(() => {
    void loader();
  }, [loader]);

  return {
    taskCounts,
    tasksLoadingStatus,
    loadEmployersWithTasks,
  };
};
